import type { SessionResponse } from '@budget/shared';

/**
 * Le jeton d'accès reste EN MÉMOIRE : jamais dans le stockage local, où le
 * moindre script tiers pourrait le lire. Ce qui survit au redémarrage, c'est le
 * cookie `httpOnly` de rafraîchissement, que le navigateur gère seul.
 */
let accessToken: string | null = null;
let refreshPromise: Promise<boolean> | null = null;
let authLostHandler: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function onAuthLost(handler: () => void): void {
  authLostHandler = handler;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function readError(response: Response): Promise<ApiError> {
  let message = `Erreur ${response.status}`;
  let details: unknown;
  try {
    const body = (await response.json()) as { error?: string; details?: unknown };
    if (typeof body.error === 'string') message = body.error;
    details = body.details;
  } catch {
    // Réponse sans corps JSON : le message par défaut fera l'affaire.
  }
  return new ApiError(response.status, message, details);
}

async function performRefresh(): Promise<boolean> {
  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'same-origin',
    });
    if (!response.ok) return false;
    const session = (await response.json()) as SessionResponse;
    accessToken = session.accessToken;
    return true;
  } catch {
    return false;
  }
}

/** Un seul rafraîchissement à la fois, même si dix requêtes échouent ensemble. */
function refresh(): Promise<boolean> {
  refreshPromise ??= performRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Désactive la tentative de rafraîchissement (routes d'authentification). */
  anonymous?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, anonymous = false } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    if (accessToken !== null && !anonymous) headers.authorization = `Bearer ${accessToken}`;

    return fetch(`/api${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let response = await send();

  // Jeton expiré : on le renouvelle en silence et on rejoue une fois.
  if (response.status === 401 && !anonymous) {
    if (await refresh()) {
      response = await send();
    } else {
      accessToken = null;
      authLostHandler?.();
      throw await readError(response);
    }
  }

  if (!response.ok) throw await readError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
